import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import Toggle from 'react-toggle';
import isEqual from 'lodash-es/isEqual';

import intlShape from '@/shared/utils/intlShape';
import RelationSearch from '@/shared/components/input/RelationSearch';
import PeriodButtonGroup, {
  getPeriodStartEndDates,
  pastToPrevMonth,
} from '@/shared/components/PeriodButtonGroup';
import enumCache from '@/shared/services/EnumCacheService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import AutoComplete from '@/shared/components/input/select/AutoComplete';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import SavedFiltersButton from '@/shared/components/SavedFiltersButton';
import userStorage from '@/shared/services/userStorage';

import DynamicFilter, {
  getDynamicFiltersFromDefaultFilters,
  handleDynamicFiltersChanged,
  initDynamicFilters,
  resetDynamicFilters,
} from '@/shared/components/DynamicFilter';
import config from '../../../config';
import api from '../../../shared/api';

import messages from '../reportingMessages';

export const defaultFilters = {
  relationId: null,
  relationName: null,
  dateFrom: moment.utc().startOf('year'),
  dateUntil: moment.utc().endOf('year'),
  period: 'year',
  relationTypes: [],
  ownCompanies: [],
  legalEntities: [],
  assistant: null,
  responsible: null,
  invoicingType: null,
  invoiceSchedule: null,
  sliding: true,
  assignmentTemplateId: null,
  withLabels: [],
  withoutLabels: [],
  teamCodes: [],
  associate: null, // UserId
  assistantBackup: null, // UserId
  billingUser: null, // UserId
  seniorUser: null, // UserId
  includeInactive: false,
  filterBehaviours: {
    withLabels: 1,
  },
  relationGroups: [],
  sectors: [],
  referenceYear: null,
};

const allDynamicFilters = [
  ...getDynamicFiltersFromDefaultFilters(defaultFilters),
  { id: 'includeInactive', label: messages.IncludeInactiveRelations },
];

const dynamicFiltersStorageKey = 'RelationRentabilityDynamicFilters';
const getIgnoredFilters = filters => {
  if (filters.period) {
    // Ignore date filters if there is a period
    return ['dateFrom', 'dateUntil'];
  }
  return [];
};

class RelationRentabilityFilters extends Component {
  state = {
    invoiceSchedule: [],
    labels: [],
    dynamicFilters: [],
  };

  static propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      relationId: PropTypes.string,
      dateFrom: momentPropTypes.momentObj,
      dateUntil: momentPropTypes.momentObj,
      period: PropTypes.oneOf(pastToPrevMonth),
      relationTypes: PropTypes.arrayOf(PropTypes.number),
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      invoicingType: PropTypes.number,
      invoiceSchedule: PropTypes.string,
      assignmentTemplateId: PropTypes.string,
      assistant: PropTypes.string,
      responsible: PropTypes.string,
      withLabels: PropTypes.arrayOf(PropTypes.string), // Guids
      withoutLabels: PropTypes.arrayOf(PropTypes.string), // Guids
      teamCodes: PropTypes.arrayOf(PropTypes.number),
      relationGroups: PropTypes.arrayOf(PropTypes.number),
      associate: PropTypes.string,
      assistantBackup: PropTypes.string,
      billingUser: PropTypes.string,
      seniorUser: PropTypes.string,
      includeInactive: PropTypes.bool,
      sectors: PropTypes.arrayOf(PropTypes.number),
    }),
    showHours: PropTypes.bool,
    onShowHoursChanged: PropTypes.func,
    intl: intlShape.isRequired,
  };
  static defaultProps = {
    filters: defaultFilters,
    showHours: false,
    onShowHoursChanged: () => {},
  };

  hasAssignments = hasAssignments();

  componentDidMount() {
    const { intl, filters } = this.props;

    this.assignmentsEnabled = hasAssignments();
    const scheduleEndpoint = `${config.appUrls.api}/api/invoiceschedule/get`;
    const labelEndpoint = `${config.appUrls.api}/api/Resource/Tags`;

    Promise.all([
      api.base.get(scheduleEndpoint),
      api.base.get(labelEndpoint),
      enumCache.get('invoicingtype'),
    ]).then(([{ data: schedules }, { data: labels }, types]) => {
      this.setState({
        invoiceSchedule: [
          {
            key: '1;-1',
            label: intl.formatMessage(messages.NoInvoicingSchedule),
          },
          {
            key: '2;11111111-1111-1111-1111-111111111111',
            label: intl.formatMessage(messages.WithInvoicingSchedule),
          },
          ...types
            .map(x => ({
              key: `1;${x.key}`,
              label: intl.formatMessage(listItemMsg('invoicingtype', x.key, x.value)),
            }))
            .sort(dynamicSort('label')),
          ...schedules
            .map(x => ({
              key: `2;${x.id}`,
              label: intl.formatMessage(listItemMsg('invoiceSchedule', x.id, x.name)),
            }))
            .sort(dynamicSort('label')),
        ],
        labels: labels
          .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase(), color: x.color }))
          .sort(dynamicSort('sort')),

        dynamicFilters: initDynamicFilters(
          intl,
          allDynamicFilters,
          userStorage.getItem(dynamicFiltersStorageKey) || [],
          filters,
          defaultFilters,
        ),
      });
    });
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) onFilterChange(newFilters);
  };
  dynamicFilterChanged = (item, selected) => {
    this.setState(({ dynamicFilters }) => {
      const { filters } = this.props;
      const newFilters = handleDynamicFiltersChanged(
        dynamicFilters,
        item,
        selected,
        filters,
        defaultFilters,
      );
      userStorage.setItem(
        dynamicFiltersStorageKey,
        newFilters.filter(x => x.selected).map(x => x.id),
      );
      if (
        !selected &&
        item.id !== 'selectAll' &&
        !isEqual(filters[item.id], defaultFilters[item.id])
      ) {
        // Reset filter when it's removed
        this.filterChanged({ [item.id]: defaultFilters[item.id] });
      }
      return { dynamicFilters: newFilters };
    });
  };
  savedFilterChanged = filters => {
    const { onFilterChange } = this.props;
    const newFilters = { ...defaultFilters, ...filters };
    if (typeof newFilters.dateFrom === 'string') newFilters.dateFrom = moment.utc(filters.dateFrom);
    if (typeof newFilters.dateUntil === 'string')
      newFilters.dateUntil = moment.utc(filters.dateUntil);

    if (newFilters.period) {
      const [dateFrom, dateUntil] = getPeriodStartEndDates(newFilters.period);
      newFilters.dateFrom = dateFrom;
      newFilters.dateUntil = dateUntil;
    }

    onFilterChange(newFilters);

    this.setState(({ dynamicFilters }) => ({
      dynamicFilters: resetDynamicFilters(dynamicFilters, newFilters, defaultFilters),
    }));
  };
  dateChanged = (dateFrom, dateUntil) => {
    this.filterChanged({ dateFrom, dateUntil, period: null });
  };
  relationChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ relationId: null, relationName: null });
    } else {
      this.filterChanged({
        relationId: item.key,
        relationName: item.label,
      });
    }
  };
  scheduleChanged = item => {
    if (!item || !item.key) {
      this.filterChanged({
        invoicingType: null,
        invoiceSchedule: null,
      });
      return;
    }
    const [type, id] = item.key.split(';');
    if (type === '1') {
      this.filterChanged({
        invoicingType: parseInt(id),
        invoiceSchedule: null,
      });
    } else if (type === '2') {
      this.filterChanged({
        invoicingType: null,
        invoiceSchedule: id,
      });
    } else {
      this.filterChanged({
        invoicingType: null,
        invoiceSchedule: null,
      });
    }
  };

  resetFilters = () => {
    this.filterChanged({ ...defaultFilters });
  };

  renderDynamicFilter = ({ type, filters, intl }) => {
    switch (type) {
      case 'includeInactive':
        return (
          <div className="col-lg-4 col-md-6">
            <div className="form-group">
              <label className="react-toggle-wrapper">
                <Toggle
                  checked={filters.includeInactive}
                  onChange={e => this.filterChanged({ includeInactive: !!e.target.checked })}
                />
                <FormattedMessage className="label-text" {...messages.IncludeInactiveRelations} />
              </label>
            </div>
          </div>
        );
      default:
        console.warn(`Missing filter: ${type}`);
        return null;
    }
  };

  render() {
    const { invoiceSchedule, labels, dynamicFilters } = this.state;
    const { filters, intl, showHours, onShowHoursChanged } = this.props;
    const initRelation = filters.relationId
      ? {
          key: filters.relationId,
          label: filters.relationName,
          invoiceStreamId: filters.invoiceStreamId,
        }
      : null;

    let selectedSchedule = null;
    if (filters.invoicingType) {
      selectedSchedule = invoiceSchedule.find(x => x.key === `1;${filters.invoicingType}`);
    } else if (filters.invoiceSchedule) {
      selectedSchedule = invoiceSchedule.find(x => x.key === `2;${filters.invoiceSchedule}`);
    }

    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);
    return (
      <React.Fragment>
        <div className="row">
          <div className="col-lg-4 col-md-6">
            <div className="form-group">
              <RelationSearch onChange={this.relationChanged} initialSelectedItem={initRelation} />
            </div>
          </div>
          <div className="col-lg-4 col-md-6">
            <div className="form-group">
              <AutoComplete
                onChange={this.scheduleChanged}
                onClear={() => this.filterChanged({ invoicingType: null, invoiceSchedule: null })}
                value={selectedSchedule}
                items={invoiceSchedule}
                placeholder={intl.formatMessage(messages.InvoiceSchedule)}
                mode="edit"
              />
            </div>
          </div>
          <div className="col-lg-4">
            <div className="form-group">
              <DelayedDateRangePicker
                startDate={filters.dateFrom}
                startDateId="RelationRentabilityFiltersDateFrom"
                endDate={filters.dateUntil}
                endDateId="RelationRentabilityFiltersDateUntil"
                onDatesChange={({ startDate, endDate }) => this.dateChanged(startDate, endDate)}
                startDatePlaceholderText={intl.formatMessage(messages.InvoiceDateFrom)}
                endDatePlaceholderText={intl.formatMessage(messages.InvoiceDateUntil)}
                block
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12">
            <div className="form-group">
              <PeriodButtonGroup
                dateFrom={filters.dateFrom}
                dateUntil={filters.dateUntil}
                period={filters.period}
                onChange={this.filterChanged}
                visiblePeriods={pastToPrevMonth}
              />
            </div>
          </div>
        </div>
        <div className="row">
          {visibleFilters && visibleFilters.length > 0
            ? visibleFilters.map(id => (
                <DynamicFilter
                  key={id}
                  type={id}
                  intl={intl}
                  labels={labels}
                  filters={filters}
                  filterChanged={this.filterChanged}
                  relationUserSearchParams={{ withUnassigned: false }}
                  labelsIdProp="id"
                  renderCustomFilter={this.renderDynamicFilter}
                />
              ))
            : null}
        </div>
        <div className="row">
          <div className="col-lg-8 col-md-6">
            <SavedFiltersButton
              screen="RelationRentabilityReport"
              currentFilters={filters}
              defaultFilters={defaultFilters}
              onFiltersChanged={this.savedFilterChanged}
              ignoreFilters={getIgnoredFilters(filters)}
              dynamicFilters={dynamicFilters}
              onDynamicFiltersChanged={this.dynamicFilterChanged}
            />
          </div>
          <div className="col-lg-4 col-md-6">
            <label className="react-toggle-wrapper margin10T">
              <Toggle checked={showHours} onChange={e => onShowHoursChanged(e.target.checked)} />
              <FormattedMessage className="label-text" {...messages.ShowHoursInsteadOfAmounts} />
            </label>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default injectIntl(RelationRentabilityFilters);
